import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOnRectangleIcon,
  BookmarkIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  ForwardIcon,
  HandThumbUpIcon,
  LightBulbIcon,
  MagnifyingGlassPlusIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MathText from '../components/MathText';
import { BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Eyebrow, INPUT, Pill } from '../components/UI/AppUI';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${API_URL}${imagePath}`;
};

// The redesigned test screen (full-width focus view inside Layout2)
const Exam2 = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [examSession, setExamSession] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [practiceTime, setPracticeTime] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [showExitModal, setShowExitModal] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [questionFlags, setQuestionFlags] = useState({});
  const [clickedButton, setClickedButton] = useState(null);
  const [userInputValue, setUserInputValue] = useState('');
  const [userInputAnswers, setUserInputAnswers] = useState({});
  const [activeTab, setActiveTab] = useState('question');
  const [similarQuestions, setSimilarQuestions] = useState([]);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [questionNotes, setQuestionNotes] = useState('');
  const [explanationExpanded, setExplanationExpanded] = useState(false);

  // Sync autoAdvance state with Header component
  useEffect(() => {
    const savedAutoAdvance = localStorage.getItem('autoAdvance') === 'true';
    setAutoAdvance(savedAutoAdvance);

    // Listen for changes from Header
    const handleAutoAdvanceChange = (event) => {
      setAutoAdvance(event.detail);
    };

    window.addEventListener('autoAdvanceChanged', handleAutoAdvanceChange);
    return () => {
      window.removeEventListener('autoAdvanceChanged', handleAutoAdvanceChange);
    };
  }, []);

  // Keyboard shortcuts for navigation (arrow keys)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in notes or other input fields
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentQuestionIndex(prev => {
          const totalQuestions = examSession?.total_questions || 0;
          return Math.min(totalQuestions - 1, prev + 1);
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [examSession?.total_questions]);

  // Fetch exam session data
  useEffect(() => {
    if (examId) {
      fetchExamSession();
    }
  }, [examId]);

  // Timer effects
  useEffect(() => {
    let interval;
    if (examSession) {
      if (examSession.mode === 'practice') {
        interval = setInterval(() => {
          setPracticeTime(prev => prev + 1);
        }, 1000);
      } else if (examSession.mode === 'real_test' && timeRemaining !== null && timeRemaining > 0) {
        interval = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev <= 1) {
              handleTimeUp();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }
    return () => clearInterval(interval);
  }, [examSession, timeRemaining]);

  // Load current question
  useEffect(() => {
    if (examSession && examSession.exam_questions && examSession.exam_questions[currentQuestionIndex]) {
      const question = examSession.exam_questions[currentQuestionIndex];
      setCurrentQuestion(question);
      loadQuestionNotes(question.question.id);
      
      // Reset question start time when loading a new question
      setQuestionStartTime(Date.now());
      
      // Reset user input value for new question, or load existing answer
      if (userInputAnswers[currentQuestionIndex]?.userInput) {
        setUserInputValue(userInputAnswers[currentQuestionIndex].userInput);
      } else if (question.user_input_answer) {
        setUserInputValue(question.user_input_answer);
      } else {
        setUserInputValue('');
      }
      
      // Initialize questionFlags state with existing flags from the question
      // Only set if we don't already have flags for this question (preserve local changes)
      setQuestionFlags(prev => {
        // If we already have flags for this question, don't overwrite them
        if (prev[question.question.id] !== undefined) {
          return prev;
        }
        
        // Only set initial flags if we don't have any yet
        return {
          ...prev,
          [question.question.id]: question.flags || []
        };
      });
      
      // Load similar questions and history for the current question
      if (question?.question?.id) {
        loadSimilarQuestions(question.question.id);
        loadQuestionHistory(question.question.id);
      }
      
      // Reset explanation expanded state when changing questions
      setExplanationExpanded(false);
    }
  }, [examSession, currentQuestionIndex]);

  const fetchExamSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // If session is paused, resume it first
        if (data.status === 'paused') {
          const resumeResponse = await fetch(`${API_URL}/api/exams/sessions/${examId}/resume/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
            }
          });
          
          if (resumeResponse.ok) {
            data.status = 'in_progress';
          }
        }
        
        setExamSession(data);
        
        if (data.mode === 'real_test' && data.time_limit_minutes) {
          const elapsed = Math.floor((new Date() - new Date(data.started_at)) / 1000);
          const totalTime = data.time_limit_minutes * 60;
          setTimeRemaining(Math.max(0, totalTime - elapsed));
        }

        // Load existing answers
        const answers = {};
        const flags = {};
        const userInputs = {};
        data.exam_questions.forEach((q, index) => {
          if (q.selected_answer) {
            answers[index] = {
              answerId: q.selected_answer.id,
              answered: true,
              isCorrect: q.selected_answer.is_correct
            };
          }
          
          // Load existing user input answers
          if (q.user_input_answer) {
            userInputs[index] = {
              userInput: q.user_input_answer,
              answered: true,
              isCorrect: q.is_correct
            };
          }
          
          // Load existing question flags (always set, even if empty)
          flags[q.question.id] = q.flags || [];
        });
        setQuestionAnswers(answers);
        setUserInputAnswers(userInputs);
        setQuestionFlags(prev => {
          // Merge with existing flags to preserve any updates made during the session
          const mergedFlags = { ...prev };
          Object.keys(flags).forEach(questionId => {
            // Only update if we don't already have flags for this question, or if the server has more recent data
            if (!mergedFlags[questionId] || mergedFlags[questionId].length === 0) {
              mergedFlags[questionId] = flags[questionId];
            }
          });
          return mergedFlags;
        });
      } else {
        setError('Failed to load exam session');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionNotes = async (questionId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/questions/${questionId}/notes/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || '');
      }
    } catch (error) {
      // Error loading notes
    }
  };

  const loadSimilarQuestions = async (questionId) => {
    try {
      const response = await fetch(`${API_URL}/api/courses/questions/${questionId}/similar/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSimilarQuestions(data || []);
      }
    } catch (error) {
      setSimilarQuestions([]);
    }
  };

  const loadQuestionHistory = async (questionId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/questions/${questionId}/history/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setQuestionHistory(data.history || []);
        setQuestionNotes(data.notes || '');
      }
    } catch (error) {
      setQuestionHistory([]);
      setQuestionNotes('');
    }
  };

  const handleAnswerSelect = async (answerId) => {
    if (isPracticeMode && questionAnswers[currentQuestionIndex]?.answered) {
      return; // Don't allow changing answers in practice mode after answering
    }

    try {
      setSavingProgress(true);
      // Use 1-based question order (currentQuestionIndex + 1)
      const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/questions/${currentQuestionIndex + 1}/answer/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          selected_answer_id: answerId,
          time_spent_seconds: Math.floor((Date.now() - questionStartTime) / 1000)
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        setQuestionAnswers(prev => ({
          ...prev,
          [currentQuestionIndex]: {
            answerId: answerId,
            answered: true,
            isCorrect: result.is_correct,
            correctAnswer: result.correct_answer || null,
            explanation: result.question_explanation || null
          }
        }));

        // Auto-advance in practice mode only if enabled
        if (isPracticeMode && autoAdvance && currentQuestionIndex < (examSession?.total_questions || 0) - 1) {
          setTimeout(() => {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          }, 1500);
        }
      }
    } catch (error) {
    } finally {
      setSavingProgress(false);
    }
  };

  const handleUserInputSubmit = async () => {
    if (!userInputValue.trim()) return;
    
    if (isPracticeMode && userInputAnswers[currentQuestionIndex]?.answered) {
      return; // Don't allow changing answers in practice mode after answering
    }

    try {
      setSavingProgress(true);
      const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/questions/${currentQuestionIndex + 1}/answer/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          user_input: userInputValue.trim(),
          time_spent_seconds: Math.floor((Date.now() - questionStartTime) / 1000)
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        setUserInputAnswers(prev => ({
          ...prev,
          [currentQuestionIndex]: {
            userInput: userInputValue.trim(),
            answered: true,
            isCorrect: result.is_correct,
            correctAnswer: result.correct_answer?.answer_text || null,
            explanation: result.question_explanation || null
          }
        }));

        // Auto-advance in practice mode only if enabled
        if (isPracticeMode && autoAdvance && currentQuestionIndex < (examSession?.total_questions || 0) - 1) {
          setTimeout(() => {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          }, 1500);
        }
      }
    } catch (error) {
    } finally {
      setSavingProgress(false);
    }
  };

  const toggleQuestionFlag = async (flagType) => {
    if (!currentQuestion?.question?.id) return;

    // Show immediate visual feedback
    setClickedButton(flagType);
    setTimeout(() => setClickedButton(null), 200);

    try {
      const response = await fetch(`${API_URL}/api/exams/questions/${currentQuestion.question.id}/mark/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          marking_type: flagType
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // The server toggles the mark; mirror its answer (marked or not) so one tap always matches it
        const apply = (flags = []) => {
          const rest = flags.filter((f) => f !== flagType);
          return result.marked ? [...rest, flagType] : rest;
        };
        setQuestionFlags((prev) => ({ ...prev, [currentQuestion.question.id]: apply(prev[currentQuestion.question.id]) }));
        setCurrentQuestion((prev) => ({ ...prev, flags: apply(prev.flags) }));
      }
    } catch (error) {
      // Error marking question
    }
  };

  const handleSaveNotes = async () => {
    if (!currentQuestion) return;
    
    try {
      await fetch(`${API_URL}/api/exams/questions/${currentQuestion.question.id}/notes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({ notes })
      });
    } catch (error) {
      // Error saving notes
    }
  };

  const handleTimeUp = () => {
    // Handle time up logic
    navigate(`/exam/${examId}/results`);
  };

  const handleSaveAndContinueLater = async () => {
    setSavingProgress(true);
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/save-progress/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      
      if (response.ok) {
        // Navigate back to subject page
        navigate(`/subjects/${examSession?.subject?.id}`);
      }
    } catch (error) {
      // Error saving progress
    } finally {
      setSavingProgress(false);
      setShowExitModal(false);
    }
  };

  const handleFinishNow = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/submit/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });

      if (response.ok) {
        navigate(`/exam/${examId}/results`);
      }
    } catch (error) {
      // Error finishing exam
    }
  };

  const handleSubmitExam = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/submit/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });

      if (response.ok) {
        navigate(`/exam/${examId}/results`);
      }
    } catch (error) {
      // Error submitting exam
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleAutoAdvance = () => {
    const next = !autoAdvance;
    setAutoAdvance(next);
    localStorage.setItem('autoAdvance', String(next));
    window.dispatchEvent(new CustomEvent('autoAdvanceChanged', { detail: next }));
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="h-10 w-64 animate-pulse rounded-lg bg-slate-200 dark:bg-white/5" />
        <div className="mt-6 h-96 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <div className={CARD}>
          <Empty icon={ExclamationTriangleIcon} title="We couldn't open this test" action={<Link to="/tests" className={BTN_PRIMARY}>Go to My tests</Link>}>{error}</Empty>
        </div>
      </div>
    );
  }

  const isPracticeMode = examSession?.mode === 'practice';
  const total = examSession?.total_questions || 0;
  const isLastQuestion = currentQuestionIndex === total - 1;
  const hasExplanation = currentQuestion?.question?.explanation && currentQuestion.question.explanation.trim() !== '';
  const hasSimilarQuestions = similarQuestions.length > 0;
  const q = currentQuestion?.question;
  const mc = questionAnswers[currentQuestionIndex];
  const ui = userInputAnswers[currentQuestionIndex];
  const answeredHere = mc?.answered || ui?.answered;
  const locked = isPracticeMode && answeredHere;
  const answeredCount = Array.from({ length: total }, (_, i) => questionAnswers[i]?.answered || userInputAnswers[i]?.answered).filter(Boolean).length;
  const flags = questionFlags[q?.id] || [];
  const lowTime = !isPracticeMode && timeRemaining !== null && timeRemaining < 300;
  const feedbackExplanation = isPracticeMode && ((mc?.answered && mc?.explanation) || (ui?.answered && ui?.explanation));
  const goTo = (i) => { setCurrentQuestionIndex(Math.max(0, Math.min(total - 1, i))); setActiveTab('question'); window.scrollTo({ top: 0 }); };

  const tabs = [
    { value: 'question', label: 'Question', enabled: true },
    { value: 'explanation', label: 'Explanation', enabled: hasExplanation },
    { value: 'similar', label: `Similar${hasSimilarQuestions ? ` (${similarQuestions.length})` : ''}`, enabled: hasSimilarQuestions },
    { value: 'progress', label: 'History & notes', enabled: true },
  ];

  const navigator = (
    <div className={`${CARD} p-5`}>
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-900 dark:text-white">Questions</p>
        <p className="text-sm tabular-nums text-slate-500 dark:text-gray-400">{answeredCount} / {total} answered</p>
      </div>
      <div className="mt-4 grid grid-cols-8 gap-1.5 sm:grid-cols-10 lg:grid-cols-5">
        {Array.from({ length: total }, (_, i) => {
          const a = questionAnswers[i] || userInputAnswers[i];
          const current = i === currentQuestionIndex;
          const done = a?.answered;
          const right = questionAnswers[i]?.isCorrect ?? userInputAnswers[i]?.isCorrect;
          let cls = 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10';
          if (done) cls = isPracticeMode && right !== undefined
            ? (right ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/15 text-rose-700 dark:text-rose-300')
            : 'bg-primary-500/15 text-primary-700 dark:text-primary-300';
          return (
            <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Question ${i + 1}${done ? ', answered' : ''}`} aria-current={current ? 'step' : undefined}
              className={`flex h-9 items-center justify-center rounded-lg text-xs font-semibold tabular-nums transition-colors ${cls} ${current ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-950' : ''}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-gray-400">
        {isPracticeMode ? (
          <>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-emerald-500/60" />Correct</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-rose-500/60" />Wrong</span>
          </>
        ) : <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-primary-500/60" />Answered</span>}
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-slate-300 dark:bg-white/15" />Not yet</span>
      </div>
      <button type="button" onClick={() => setShowExitModal(true)} className={`${BTN_SECONDARY} mt-5 w-full`}>
        <ArrowLeftOnRectangleIcon className="h-4 w-4" />Save or finish
      </button>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-50 text-slate-600 dark:bg-slate-950 dark:text-gray-300">
      {/* Test bar */}
      <div className="sticky top-16 z-20 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-white/10 dark:bg-slate-950/90">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <button type="button" onClick={() => setShowExitModal(true)} aria-label="Exit test"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{examSession?.subject?.name || examSession?.title || 'Test'}</p>
              <Pill tone={isPracticeMode ? 'blue' : 'amber'} className="hidden sm:inline-flex">{isPracticeMode ? 'Practice' : 'Real test'}</Pill>
            </div>
            <div className="mt-1.5 flex items-center gap-3">
              <span className="h-1 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                <span className="block h-full rounded-full bg-primary-500 transition-all" style={{ width: `${total ? (answeredCount / total) * 100 : 0}%` }} />
              </span>
              <span className="flex-shrink-0 text-xs tabular-nums text-slate-500 dark:text-gray-400">{answeredCount}/{total}</span>
            </div>
          </div>
          {isPracticeMode && (
            <button type="button" onClick={toggleAutoAdvance} aria-pressed={autoAdvance} title="Go to the next question after answering"
              className={`hidden items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors sm:flex ${autoAdvance ? 'bg-primary-500/10 text-primary-700 dark:text-primary-300' : 'text-slate-500 hover:bg-slate-100 dark:text-gray-400 dark:hover:bg-white/10'}`}>
              <ForwardIcon className="h-4 w-4" />Auto-next
            </button>
          )}
          <span className={`flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 font-mono text-sm font-semibold tabular-nums ${lowTime ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400' : 'bg-slate-100 text-slate-700 dark:bg-white/5 dark:text-gray-200'}`}
            aria-label={isPracticeMode ? 'Time spent' : 'Time left'}>
            <ClockIcon className="h-4 w-4" />
            {isPracticeMode ? formatTime(practiceTime) : timeRemaining !== null ? formatTime(timeRemaining) : '0:00'}
          </span>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl items-start gap-6 px-4 pb-32 pt-6 lg:grid-cols-[minmax(0,1fr)_288px]">
        <section className="min-w-0">
          {/* Question header */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <Eyebrow>Question {currentQuestionIndex + 1} of {total}</Eyebrow>
              {q?.question_number && <p className="mt-1 text-xs text-slate-400 dark:text-gray-500" title="Quote this reference if you report a problem with this question">Ref. {q.question_number}</p>}
            </div>
            <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Question views">
              {tabs.map((t) => (
                <button key={t.value} type="button" role="tab" aria-selected={activeTab === t.value} disabled={!t.enabled} onClick={() => setActiveTab(t.value)}
                  className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${activeTab === t.value
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                    : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`${CARD} mt-4 p-5 sm:p-7`}>
            {activeTab === 'question' && (
              <>
                {q?.annex_image && (
                  <button type="button" onClick={() => setShowImageModal(true)} className="group relative mb-5 block w-full overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10" aria-label="Enlarge figure">
                    <img src={getImageUrl(q.annex_image)} alt="Question figure" className="mx-auto max-h-[38vh] object-contain" />
                    <span className="absolute right-2 top-2 flex items-center gap-1 rounded-lg bg-slate-950/70 px-2 py-1 text-xs font-medium text-white"><MagnifyingGlassPlusIcon className="h-4 w-4" />Enlarge</span>
                  </button>
                )}
                <p className="whitespace-pre-line text-lg font-medium leading-relaxed text-slate-900 sm:text-xl dark:text-white">{q?.question_text}</p>

                {q?.question_type === 'USER_INPUT' ? (
                  <div className="mt-6">
                    <div className="flex max-w-sm gap-2">
                      <input type="text" value={userInputValue} onChange={(e) => setUserInputValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !ui?.answered) handleUserInputSubmit(); }}
                        placeholder="Type your answer" aria-label="Your answer" disabled={locked}
                        className={`flex-1 rounded-xl border bg-white px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:opacity-70 dark:bg-white/[0.04] dark:text-white ${ui?.answered
                          ? (isPracticeMode ? (ui.isCorrect ? 'border-emerald-500' : 'border-rose-500') : 'border-primary-500')
                          : 'border-slate-300 focus:border-primary-500 dark:border-white/15'}`} />
                      <button type="button" onClick={handleUserInputSubmit} disabled={locked || savingProgress || !userInputValue.trim()} className={BTN_PRIMARY}>
                        {savingProgress ? 'Saving…' : 'Submit'}
                      </button>
                    </div>
                    {ui?.answered && (
                      isPracticeMode ? (
                        <p className={`mt-3 flex items-center gap-2 text-sm font-semibold ${ui.isCorrect ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          {ui.isCorrect ? <><CheckCircleIcon className="h-5 w-5" />Correct</> : <><XCircleIcon className="h-5 w-5" />Not quite. The answer is <span className="font-bold">{ui.correctAnswer}</span></>}
                        </p>
                      ) : <p className="mt-3 text-sm font-semibold text-primary-600 dark:text-primary-300">Answer saved</p>
                    )}
                  </div>
                ) : (
                  <div className="mt-6 space-y-3">
                    {q?.answers?.map((answer, index) => {
                      const selected = mc?.answerId === answer.id;
                      const showRight = locked && answer.is_correct;
                      const showWrong = locked && selected && !answer.is_correct;
                      let cls = 'border-slate-200 hover:border-primary-400 hover:bg-primary-500/[0.04] dark:border-white/10 dark:hover:border-primary-500/60';
                      let tile = 'bg-slate-100 text-slate-600 group-hover:bg-primary-600 group-hover:text-white dark:bg-white/10 dark:text-gray-300';
                      if (showRight) { cls = 'border-emerald-500 bg-emerald-500/10'; tile = 'bg-emerald-500 text-white'; }
                      else if (showWrong) { cls = 'border-rose-500 bg-rose-500/10'; tile = 'bg-rose-500 text-white'; }
                      else if (locked) { cls = 'border-slate-200 opacity-60 dark:border-white/10'; tile = 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-gray-400'; }
                      else if (selected) { cls = 'border-primary-500 bg-primary-500/10 ring-1 ring-primary-500'; tile = 'bg-primary-600 text-white'; }
                      return (
                        <button key={answer.id} type="button" onClick={() => handleAnswerSelect(answer.id)} disabled={locked} aria-pressed={selected}
                          className={`group flex w-full items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-colors disabled:cursor-default ${cls}`}>
                          <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold transition-colors ${tile}`}>{String.fromCharCode(65 + index)}</span>
                          <span className="flex-1 text-[15px] leading-snug text-slate-800 dark:text-gray-100">{answer.answer_text}</span>
                          {showRight && <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-emerald-500" />}
                          {showWrong && <XCircleIcon className="h-6 w-6 flex-shrink-0 text-rose-500" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {feedbackExplanation && (
                  <div className="mt-6 overflow-hidden rounded-xl border border-primary-500/30">
                    <button type="button" onClick={() => setExplanationExpanded(!explanationExpanded)} aria-expanded={explanationExpanded}
                      className="flex w-full items-center justify-between bg-primary-500/10 px-4 py-3 text-sm font-semibold text-primary-800 dark:text-primary-200">
                      <span className="flex items-center gap-2"><LightBulbIcon className="h-5 w-5" />Why?</span>
                      <ChevronDownIcon className={`h-5 w-5 transition-transform ${explanationExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {explanationExpanded && (
                      <div className="px-4 py-4">
                        <MathText text={mc?.explanation || ui?.explanation} className="whitespace-pre-line text-sm leading-relaxed text-slate-700 dark:text-gray-300" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {activeTab === 'explanation' && hasExplanation && (
              <>
                <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white"><LightBulbIcon className="h-5 w-5 text-primary-500" />Explanation</h2>
                <MathText text={q.explanation} className="mt-3 block whitespace-pre-line leading-relaxed text-slate-700 dark:text-gray-300" />
              </>
            )}

            {activeTab === 'similar' && hasSimilarQuestions && (
              <ol className="space-y-5">
                {similarQuestions.map((sq, i) => (
                  <li key={sq.id} className="flex gap-4">
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-primary-500/10 text-xs font-bold text-primary-700 dark:text-primary-300">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="whitespace-pre-line text-slate-900 dark:text-white">{sq.question_text}</p>
                      <div className="mt-3 space-y-1.5">
                        {(sq.answers || []).map((a, j) => (
                          <p key={a.id} className={`rounded-lg px-3 py-2 text-sm ${a.is_correct ? 'bg-emerald-500/10 font-medium text-emerald-800 dark:text-emerald-200' : 'text-slate-600 dark:text-gray-400'}`}>
                            <span className="mr-2 font-semibold">{String.fromCharCode(65 + j)}.</span>{a.answer_text}
                          </p>
                        ))}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {activeTab === 'progress' && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-semibold text-slate-900 dark:text-white">Your attempts</h2>
                  {questionHistory.length ? (
                    <ul className="mt-3 divide-y divide-slate-100 dark:divide-white/5">
                      {questionHistory.map((a, i) => (
                        <li key={i} className="flex items-center justify-between py-2.5 text-sm">
                          <span className="flex items-center gap-3">
                            <Pill tone={a.is_correct ? 'green' : 'red'}>{a.is_correct ? 'Correct' : 'Wrong'}</Pill>
                            <span className="text-slate-500 dark:text-gray-400">{new Date(a.attempted_at).toLocaleDateString()}</span>
                          </span>
                          <span className="tabular-nums text-slate-500 dark:text-gray-400">{a.time_spent_seconds}s</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">This is your first time on this question.</p>}
                </div>
                <label className="block">
                  <span className="font-semibold text-slate-900 dark:text-white">Your notes</span>
                  <span className="mt-0.5 block text-xs text-slate-500 dark:text-gray-400">Saved automatically when you click away.</span>
                  <textarea value={questionNotes} onChange={(e) => setQuestionNotes(e.target.value)} rows={5}
                    onBlur={async () => {
                      try {
                        await fetch(`${API_URL}/api/exams/questions/${q?.id}/notes/`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` },
                          body: JSON.stringify({ notes: questionNotes }),
                        });
                      } catch (err) {
                        console.error('Failed to save notes:', err);
                      }
                    }}
                    placeholder="Anything worth remembering about this question…" className={`${INPUT} mt-2`} />
                </label>
              </div>
            )}
          </div>

          <div className="mt-6 lg:hidden">{navigator}</div>
        </section>

        <aside className="hidden lg:sticky lg:top-36 lg:block">{navigator}</aside>
      </div>

      {/* Action bar */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur dark:border-white/10 dark:bg-slate-950/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-3">
          <button type="button" onClick={() => goTo(currentQuestionIndex - 1)} disabled={currentQuestionIndex === 0} className={`${BTN_SECONDARY} px-3 disabled:opacity-40 sm:px-5`}>
            <ChevronLeftIcon className="h-5 w-5" /><span className="hidden sm:inline">Previous</span>
          </button>
          <div className="flex items-center gap-2">
            {[
              { type: 'easy', label: 'Easy', icon: HandThumbUpIcon, on: 'border-emerald-500 bg-emerald-500 text-white', idle: 'hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400' },
              { type: 'difficult', label: 'Hard', icon: ExclamationTriangleIcon, on: 'border-rose-500 bg-rose-500 text-white', idle: 'hover:border-rose-400 hover:text-rose-600 dark:hover:text-rose-400' },
            ].map(({ type, label, icon: Icon, on, idle }) => {
              const active = flags.includes(type);
              return (
                <button key={type} type="button" onClick={() => toggleQuestionFlag(type)} aria-pressed={active} title={`Mark as ${label.toLowerCase()}`}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${active ? on : `border-slate-300 text-slate-500 dark:border-white/15 dark:text-gray-400 ${idle}`} ${clickedButton === type ? 'scale-95' : ''}`}>
                  <Icon className="h-4 w-4" />{label}
                </button>
              );
            })}
          </div>
          {!isLastQuestion ? (
            <button type="button" onClick={() => goTo(currentQuestionIndex + 1)} className={`${BTN_PRIMARY} px-3 sm:px-5`}>
              <span className="hidden sm:inline">Next</span><ChevronRightIcon className="h-5 w-5" />
            </button>
          ) : (
            <button type="button" onClick={handleSubmitExam} className={`${BTN_PRIMARY} px-3 sm:px-5`}>
              <FlagIcon className="h-5 w-5" />Submit
            </button>
          )}
        </div>
      </div>

      {showExitModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={() => setShowExitModal(false)}>
          <div role="dialog" aria-modal="true" aria-label="Leave this test" className={`${CARD} w-full max-w-md bg-white p-6 shadow-2xl dark:bg-slate-900`} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Leave this test?</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">You've answered {answeredCount} of {total} questions.</p>
            <div className="mt-6 space-y-3">
              <button type="button" onClick={handleSaveAndContinueLater} disabled={savingProgress}
                className="flex w-full items-start gap-3 rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-500/5 disabled:opacity-50 dark:border-white/10">
                <BookmarkIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                <span><span className="block font-semibold text-slate-900 dark:text-white">{savingProgress ? 'Saving…' : 'Save and continue later'}</span><span className="block text-sm text-slate-500 dark:text-gray-400">Pick up where you left off from My tests.</span></span>
              </button>
              <button type="button" onClick={handleFinishNow}
                className="flex w-full items-start gap-3 rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-emerald-400 hover:bg-emerald-500/5 dark:border-white/10">
                <FlagIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" />
                <span><span className="block font-semibold text-slate-900 dark:text-white">Finish and see results</span><span className="block text-sm text-slate-500 dark:text-gray-400">Unanswered questions count as wrong.</span></span>
              </button>
            </div>
            <button type="button" onClick={() => setShowExitModal(false)} className={`${BTN_SECONDARY} mt-4 w-full`}>Keep going</button>
          </div>
        </div>
      )}

      {showImageModal && q?.annex_image && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-slate-950/95" onClick={() => setShowImageModal(false)}>
          <div className="flex justify-end p-3">
            <button type="button" onClick={() => setShowImageModal(false)} aria-label="Close" className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><XMarkIcon className="h-6 w-6" /></button>
          </div>
          <div className="flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <img src={getImageUrl(q.annex_image)} alt="Question figure, enlarged" className="mx-auto max-w-none rounded-lg bg-white sm:max-w-full" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Exam2;
